import React from 'react';
import PropTypes from 'prop-types';

import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import { useLob } from '~/components/hooks/useLob';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { useLobConfiguredFields } from '~/components/hooks/useLobConfiguredFields';
import ConfiguredTablesSection from '~/components/Policy/ConfiguredTablesSection/ConfiguredTablesSection';

import { CONFIGURATION_FEATURES_NAMES } from '../../../Types';
import { isClaimPolicyManuallyFilled, isFeatureEnabled } from '../../../Utils';
import { getEntityIcon } from '../../../Utils/ClaimUtils';
import { useClaim } from '../../ClaimContainer';
import { FsButton, PERMISSION_ACTIONS, PERMISSION_VERBS, RestrictedPermissions } from '../../core';
import { useCms } from '../../hooks/useCms';
import useOrganization from '../../OrganizationContext';

import UnlistedVehicleCard from './CoveredEntity/AutoSpecific/UnlistedVehicleCard';
import CoveredEntityCard from './CoveredEntity/CoveredEntityCard';
import CoveredObjectTypeSelector from './CoveredEntity/CoveredObjectTypeSelector/CoveredObjectTypeSelector';
import EditCoveredEntityDialog from './CoveredEntity/EditCoveredEntityDialog/EditCoveredEntityDialog';
import FirstPartyPedestrianWrapper from './CoveredEntity/FirstPartyPedestrianWrapper';
import UnlistedCoveredObjectCard from './CoveredEntity/UnlistedCoveredObjectCard/UnlistedCoveredObjectCard';
import CapacityAgreements from './PolicyDetails/CapacityAgreements/CapacityAgreements';
import PolicyDetailsCard from './PolicyDetails/PolicyDetailsCard';
import { useAddCoveredObjectFlow } from './useAddCoveredObjectFlow';

import { useStyles } from '../../../assets/styles';
import styles from './GenericPolicyPage.module.scss';

const getAutoFieldsConfig = ({
  incidentConfiguration,
  coveredObject = null,
  vehicleTypesDict = null,
  unlisted = false,
}) => {
  const coveredObjectTypeLabel = unlisted ? 'Unlisted First Party' : 'Covered Vehicle';
  const Icon = getEntityIcon('vehicle_icon');
  const coveredObjectType = 'covered_vehicle';
  const vehicleTypesList = Object.keys(vehicleTypesDict).map((id) => ({ id, desc: vehicleTypesDict[id].desc }));

  const fields = [
    { id: 'year', type: 'year', desc: 'Year', active: true },
    { id: 'make', type: 'string', desc: 'Make', active: true },
    { id: 'model', type: 'string', desc: 'Model', active: true },
    { id: 'series', type: 'string', desc: 'Series', active: true },
    { id: 'vin', type: 'string', desc: 'VIN', active: true },
    { id: 'plate_number', type: 'string', desc: 'Plate Number', active: true },
    { id: 'vehicle_plate_state', type: 'us_state_select', desc: 'Registration State', active: true },
    { id: 'vehicle_owner_contact_id', type: 'contact', desc: 'Owner', active: true },
    { id: 'vehicle_type', type: 'select', desc: 'Vehicle Type', options: vehicleTypesList, active: true },
    { id: 'mileage', type: 'number', desc: 'Mileage', active: true },
    { id: 'ownership_status', type: 'string', desc: 'Ownership Status', active: true },
    { id: 'color', type: 'string', desc: 'Color', active: true },
    { id: 'fuel_type', type: 'string', desc: 'Fuel Type', active: true },
    { id: 'vehicle_value', type: 'monetary', desc: 'Vahicle Value', active: true },
    { id: 'vehicle_cost_new', type: 'monetary', desc: 'New Vahicle Value', active: true },
    { id: 'engine_type', type: 'string', desc: 'Engine Type', active: true },
    { id: 'engine_capacity', type: 'number', desc: 'Engine Capacity', active: true },
    { id: 'number_of_seats', type: 'number', desc: 'Number Of Seats', active: true },
  ];

  const overrideTypes = ['year'];

  const defaultFields = fields.map((field) => {
    const vehicleConf = incidentConfiguration?.policy?.covered_vehicle?.vehicle ?? {};
    return {
      ...field,
      ...vehicleConf[field.id],
      type: overrideTypes.includes(field?.type) ? field?.type : vehicleConf[field.id]?.type || field?.type,
      id: `vehicle.${field.id}`,
      value: coveredObject?.vehicle[field.id],
    };
  });

  const configuredFields =
    incidentConfiguration?.policy?.covered_vehicle?.configured_fields
      ?.filter((field) => field.active)
      .map((field) => {
        let value = coveredObject?.configured_fields_values?.[field.id] ?? '';
        if (!value) {
          if (field.type === 'location') {
            value = {};
          }
          if (field.type === 'multiselect') {
            value = [];
          }
        }
        return { ...field, value };
      }) ?? [];
  return { Icon, coveredObjectTypeLabel, configuredFields, coveredObjectType, defaultFields };
};

const GeneralCoveredObjectWrapper = ({ coveredObject, isFirstParty = false }) => {
  const { Icon, coveredObjectTypeLabel, configuredFields, defaultFields } = useLobConfiguredFields(coveredObject, true);

  return (
    <CoveredEntityCard
      icon={Icon && <Icon />}
      coveredObjectTypeLabel={coveredObjectTypeLabel}
      defaultFields={defaultFields}
      configuredFields={configuredFields}
      coveredObjectIdForPatchEndpoints={coveredObject.id}
      coveredObject={coveredObject}
      isFirstParty={isFirstParty}
      fetchCoveredEntityEndpoint={`/api/v1/policies/${coveredObject.policy_id}/covered_entities/${coveredObject.covered_entity_id}`}
    />
  );
};

GeneralCoveredObjectWrapper.propTypes = {
  coveredObject: PropTypes.object.isRequired,
  isFirstParty: PropTypes.bool,
};

const CoveredVehicleWrapper = ({ coveredObject, isFirstParty = false }) => {
  const { incidentConfiguration } = useIncidentConfiguration();
  const { vehicleTypesDict } = useOrganization();
  const { Icon, coveredObjectTypeLabel, configuredFields, defaultFields } = getAutoFieldsConfig({
    incidentConfiguration,
    coveredObject,
    vehicleTypesDict,
  });

  return (
    <CoveredEntityCard
      icon={Icon && <Icon />}
      coveredObjectTypeLabel={coveredObjectTypeLabel}
      defaultFields={defaultFields}
      configuredFields={configuredFields}
      coveredObjectIdForPatchEndpoints={coveredObject.id}
      coveredObject={coveredObject}
      isFirstParty={isFirstParty}
      fetchCoveredEntityEndpoint={`/api/v1/policies/${coveredObject.policy_id}/covered_entities/${coveredObject.covered_entity_id}`}
    />
  );
};

CoveredVehicleWrapper.propTypes = {
  coveredObject: PropTypes.object.isRequired,
  isFirstParty: PropTypes.bool,
};

const GenericPolicyPage = () => {
  const { classes } = useStyles();
  const { userOrganization } = useCms();
  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });
  const { lob } = useLob();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { coverageConfiguration, organizationId } = useOrganization();
  const isAutoClaim = lob === 'auto_claim';
  const coveredObjectsKey = isAutoClaim ? 'covered_vehicles' : 'covered_objects';
  const coveredObjects = claim.policy[coveredObjectsKey];

  // Handle multiple covered object types
  const { lobConfigurationsDict } = useLobConfiguration();
  const availableCoveredObjectTypes = lobConfigurationsDict[lob]?.covered_objects || [];
  const isConfigurableCoveragesEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_COVERAGES
  );
  const { vehicleTypesDict } = useOrganization();
  const isManualPolicy = isClaimPolicyManuallyFilled(claim);

  // Shared with V2 — owns the Add-flow state machine + POST. V1 doesn't need
  // a post-create refresh (claim payload already carries the rows), so
  // onCreated is omitted. V1 also ignores the hook's `availableTypes` because
  // it uses the raw lob list (without the auto override) for both the button
  // label heuristic and the type selector.
  const {
    addType,
    showTypeSelector,
    isCreating: isSaving,
    handleAdd: handleAddCoveredObject,
    handleAddTypeSelected: handleTypeSelected,
    handleCreate: handleCreateNewCoveredEntity,
    closeTypeSelector,
    closeAddDialog,
  } = useAddCoveredObjectFlow({
    isAutoClaim,
    rawAvailableTypes: availableCoveredObjectTypes,
    claimId: claim.id,
    isManualPolicy,
    onAsyncClaimUpdate,
  });

  // Get configuration for the type being added (or first type as fallback so
  // the button label stays correct before the user clicks).
  const activeCoveredObjectType = addType || availableCoveredObjectTypes[0]?.id;
  let { icon, coveredObjectTypeLabel, configuredFields, defaultFields, isProperty } = useLobConfiguredFields(
    activeCoveredObjectType ? { type: activeCoveredObjectType } : null,
    false
  );

  coveredObjects?.sort((x, y) => x.id - y.id);

  if (isAutoClaim) {
    const autoSpecificFields = getAutoFieldsConfig({ incidentConfiguration, coveredObject: null, vehicleTypesDict });
    icon = autoSpecificFields.icon;
    coveredObjectTypeLabel = autoSpecificFields.coveredObjectTypeLabel;
    configuredFields = autoSpecificFields.configuredFields;
    defaultFields = autoSpecificFields.defaultFields;
    coveredObjects?.sort((x, y) => (y?.id === claim?.covered_vehicle_id ? 1 : -1));
  } else {
    const parties = isProperty ? claim?.incident?.other_properties_parties : claim?.incident?.involved_person_parties;
    const firstParty = parties?.find((involved) => involved?.is_first_party);
    const firstPartyCoveredObjectId = (isProperty ? firstParty?.generic_property_involved : firstParty?.involved_person)
      ?.covered_object_id;
    coveredObjects?.sort((x, y) => (y?.id === firstPartyCoveredObjectId ? 1 : -1));
  }

  return (
    <div className={styles.policyPageContainer}>
      <Grid container className={styles.policyPage}>
        <Grid container alignItems="center">
          <Grid item xs={6}>
            <Typography display="block" variant="h5" className={styles.policyPageHeader}>
              Policy
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <div className={classes.buttonsContainer}>
              {(isAutoClaim || availableCoveredObjectTypes.length > 0) && (
                <RestrictedPermissions
                  action={isManualPolicy ? PERMISSION_ACTIONS.MANUAL_POLICY : PERMISSION_ACTIONS.API_POLICY}
                  verb={PERMISSION_VERBS.WRITE}
                >
                  <FsButton color="primary" onClick={handleAddCoveredObject}>
                    <AddIcon className={classes.leftButtonIcon} />
                    {availableCoveredObjectTypes.length > 1 ? 'Add Covered Object' : `Add ${coveredObjectTypeLabel}`}
                  </FsButton>
                </RestrictedPermissions>
              )}
            </div>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <div className={classes.shadowedCardDivRow}>
            <PolicyDetailsCard claim={claim} onClaimUpdate={onAsyncClaimUpdate} />
          </div>
          {coverageConfiguration?.is_capacity_agreement_enabled ? (
            <div className={classes.shadowedCardDivRow}>
              <CapacityAgreements claim={claim} organizationId={organizationId} onUpdateSuccess={onAsyncClaimUpdate} />
            </div>
          ) : null}
        </Grid>
        <Grid item xs={6}>
          {isAutoClaim && claim?.unlisted_covered_vehicle && (
            <div className={classes.shadowedCardDivRow}>
              <UnlistedVehicleCard
                claim={claim}
                onClaimUpdate={onAsyncClaimUpdate}
                coveredVehicle={claim?.unlisted_covered_vehicle}
                isRental={claim?.is_unlisted_vehicle_rental}
              />
            </div>
          )}
          {claim?.unlisted_covered_objects?.map((unlistedCoveredObject) => (
            <div key={unlistedCoveredObject.id} className={classes.shadowedCardDivRow}>
              <UnlistedCoveredObjectCard
                claim={claim}
                coveredObject={unlistedCoveredObject}
                onClaimUpdate={onAsyncClaimUpdate}
              />
            </div>
          ))}
          {coveredObjects?.map((coveredObject, i) => (
            <div key={i} className={classes.shadowedCardDivRow}>
              {isAutoClaim ? (
                <CoveredVehicleWrapper coveredObject={coveredObject} isFirstParty={i === 0} />
              ) : (
                <GeneralCoveredObjectWrapper coveredObject={coveredObject} isFirstParty={i === 0} />
              )}
            </div>
          ))}

          {isConfigurableCoveragesEnabled && claim.pedestrian_covered_entity_id && (
            <div className={classes.shadowedCardDivRow}>
              <FirstPartyPedestrianWrapper />
            </div>
          )}
        </Grid>
        <ConfiguredTablesSection policy={claim.policy} />
      </Grid>

      <CoveredObjectTypeSelector
        open={showTypeSelector}
        availableTypes={availableCoveredObjectTypes}
        onTypeSelected={handleTypeSelected}
        onClose={closeTypeSelector}
      />

      {addType && (
        <EditCoveredEntityDialog
          isUpdate={false}
          onClose={closeAddDialog}
          icon={icon}
          coveredObjectTypeLabel={coveredObjectTypeLabel}
          onSaveClick={handleCreateNewCoveredEntity}
          isSaving={isSaving}
          configuredFields={configuredFields}
          defaultFields={defaultFields}
        />
      )}
    </div>
  );
};

export { getAutoFieldsConfig };
export default GenericPolicyPage;
